/**
 * Matching a search query against a name the way the directory filter does.
 *
 * Kept apart from the component because it is pure, fiddly, and worth testing:
 * the index mapping below is the kind of code that looks right and is not.
 */

/**
 * Fold a string the way `useFilter({ sensitivity: "base" })` compares: case
 * and accents removed. Returns the folded text plus a map from each folded
 * character back to the index of the ORIGINAL character that produced it.
 *
 * The map is the whole point. "Münch" folds to "munch", and the fold is not
 * length-preserving -- ü decomposes to u plus a combining mark, which is then
 * dropped -- so a match found at folded position 1 has to be sliced out of the
 * original at its own index, or the highlight lands on the wrong letters.
 */
export function foldForSearch(value: string): { folded: string; indexMap: number[] } {
  let folded = "";
  const indexMap: number[] = [];

  for (let index = 0; index < value.length; index += 1) {
    const decomposed = value[index]!.normalize("NFD").toLowerCase();
    for (const character of decomposed) {
      // Combining marks carry the accent; dropping them is what makes
      // "munch" match "Münch".
      if (/\p{M}/u.test(character)) {
        continue;
      }
      folded += character;
      indexMap.push(index);
    }
  }

  return { folded, indexMap };
}

/**
 * The fold alone, for deciding whether something matches rather than where.
 * A row's searchable text is folded once and kept; the query once per change.
 */
export function fold(value: string): string {
  return foldForSearch(value).folded;
}

export type HighlightChunkPart = { text: string; match: boolean };

/**
 * Split `text` into matched and unmatched runs against `query`, using the same
 * comparison the filter uses.
 *
 * Chakra's `Highlight` is not used here: it matches literally, so a list
 * filtered accent-insensitively would show rows with nothing marked -- type
 * "munch", see Münch in the results, and wonder why it is there.
 */
export function highlightChunks(text: string, query: string): HighlightChunkPart[] {
  const trimmed = query.trim();
  if (trimmed === "") {
    return [{ text, match: false }];
  }

  const { folded, indexMap } = foldForSearch(text);
  const { folded: needle } = foldForSearch(trimmed);
  if (needle === "") {
    return [{ text, match: false }];
  }

  const chunks: HighlightChunkPart[] = [];
  let cursor = 0;
  let searchFrom = 0;

  for (;;) {
    const hit = folded.indexOf(needle, searchFrom);
    if (hit === -1) {
      break;
    }
    const start = indexMap[hit]!;
    // The end of the original character that produced the last folded one, so
    // a trailing accented letter is included whole.
    const end = (indexMap[hit + needle.length - 1] ?? start) + 1;

    if (start > cursor) {
      chunks.push({ text: text.slice(cursor, start), match: false });
    }
    chunks.push({ text: text.slice(start, end), match: true });
    cursor = end;
    searchFrom = hit + needle.length;
  }

  if (cursor < text.length) {
    chunks.push({ text: text.slice(cursor), match: false });
  }
  return chunks;
}
